// examenLugar.js — recordatorio de examen con LUGAR y QUÉ LLEVAR (26/10/2026, Autoescola Garrotxa: «que al alumno le
// llegue la hora, la ubicación y lo que tiene que llevar»). Ficha: docs/features/autoescuela-examen-lugar-y-que-llevar.md
//
//   · c_autoescuela_config.examen_lugar = { enabled, teorico: { lugar, llevar, por_permiso }, pista: {…}, circulacion: {…} }:
//     el lugar y lo que hay que llevar POR PRUEBA. Apagado de fábrica (sin `enabled: true`, el recordatorio de siempre).
//   · `por_permiso` (26/10/2026, Garrotxa: «la moto, a l'autoescola o a Begudà; el camió, a Begudà; el B, a
//     l'autoescola»): dentro de cada prueba, textos propios por GRUPO de permiso { moto, coche, camion, bus } →
//     { lugar, llevar }. Cada campo vacío cae al de la prueba (el de la prueba es el valor por defecto).
//   · El permiso del examen: el del examen (`permiso`, o el que trajo la DGT en `aues.permiso`) y, si no consta, el de la
//     ficha del alumno (`carnet_type` y, detrás, `carnets_en_curso`: el primero con texto propio para esa prueba).
//   · c_examenes.lugar (lo escribe la oficina en el examen o lo trae la DGT por AUES, `detalleCita`) MANDA sobre el de
//     la configuración. Sin ninguno de los dos: «te lo confirmará la autoescuela».
//   · «Qué llevar» vacío → el texto sugerido (DNI original en vigor y gafas o lentillas). Meta no admite un parámetro
//     vacío, así que la plantilla nueva SIEMPRE lleva algo en {{5}} y {{6}}.
//   · Meta tampoco admite en un parámetro saltos de línea, tabuladores ni más de 4 espacios seguidos: lo que escriba la
//     autoescuela se APLANA a una línea (`textoPlano`: cada línea se une con « · », o con un espacio si ya acaba en
//     punto). Y el cuerpo entero no pasa de 1.024 caracteres: con los máximos de aquí (lugar 300, llevar 250, alumno
//     60) y un negocio de hasta ~90 caracteres, cabe.
//   · La plantilla: examen_recordatorio_lugar_crm (lógica) → examen_recordatorio_lugar_crm_trial (es + ca, mismo
//     nombre). La eligen c_putExamen (aviso al apuntarlo) y c_schedulerAlumnoAvisos (recordatorio N días antes) solo
//     con su env EXAMEN_LUGAR_ENABLED=1 y la casilla encendida en la autoescuela.
//
// PURO (sin AWS ni fechas del sistema). CANÓNICO: se edita SOLO aquí y luego
//   node aws_lambdas/_shared_autoescuela/examenEsperaCopias.js
// (copia por valor en c_putExamen, c_putAutoescuelaConfig y c_schedulerAlumnoAvisos; en módulo ES en la app y en el
// simulador). examenEsperaCopias.test.js falla si alguna diverge.

import { descripcionExamen, fechaLarga } from "./examenEspera";

const PRUEBAS_LUGAR = ["teorico", "pista", "circulacion"];
const LUGAR_MAX = 300;
const LLEVAR_MAX = 250;
const ALUMNO_MAX = 60;
/** Límite de Meta para el cuerpo entero de la plantilla (ya con los parámetros puestos). */
const CUERPO_MAX = 1024;
const PLANTILLA_LUGAR = "examen_recordatorio_lugar_crm";

/** Grupos de permiso con texto propio, en el orden en que se enseñan. */
const GRUPOS_PERMISO = ["coche", "moto", "camion", "bus"];
/** Los permisos de cada grupo (lo que se enseña en la tarjeta; `grupoDePermiso` acepta también «CE», «C1 + E»…). */
const PERMISOS_DE_GRUPO = {
  coche: ["B", "B+E"],
  moto: ["AM", "A1", "A2", "A"],
  camion: ["C1", "C1+E", "C", "C+E"],
  bus: ["D1", "D1+E", "D", "D+E"],
};

/** Lo que se sugiere llevar si la autoescuela no escribe nada (vale para cualquier examen de la DGT). */
const LLEVAR_DEFECTO = {
  es: "DNI original en vigor y, si las usas, gafas o lentillas",
  ca: "DNI original vigent i, si en fas servir, ulleres o lents de contacte",
};
/** {{5}} cuando no hay lugar ni en el examen ni en la configuración. */
const LUGAR_PENDIENTE = {
  es: "te lo confirmará la autoescuela",
  ca: "te'l confirmarà l'autoescola",
};
const HORA_PENDIENTE = { es: " (hora por confirmar)", ca: " (hora per confirmar)" };

const esObjeto = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const idioma = (lang) => (lang === "ca" ? "ca" : "es");

/**
 * Lo que escribe la autoescuela → UNA línea apta para un parámetro de Meta (sin saltos de línea, tabuladores ni
 * espacios repetidos). Cada línea se une a la anterior con « · » (o con un espacio si la anterior ya acaba en . ! ? : ;
 * o ,); las viñetas del principio («- », «• ») se quitan. Sin recortar.
 */
function textoPlano(s) {
  const lineas = String(s == null ? "" : s)
    .replace(/\t+/g, " ")
    .split(/\r\n|\r|\n/)
    .map((l) => l.replace(/^\s*(?:[-•*·]\s+)+/, "").replace(/ {2,}/g, " ").trim())
    .filter(Boolean);
  let out = "";
  for (const l of lineas) {
    if (!out) out = l;
    else out += /[.!?:;,]$/.test(out) ? ` ${l}` : ` · ${l}`;
  }
  return out;
}

/** ¿Lo escrito cambia al pasar a una línea? (saltos de línea, tabuladores o espacios repetidos) → avisarlo en el editor. */
function cambiaAlAplanar(s) {
  const t = String(s == null ? "" : s);
  return t.trim() !== "" && textoPlano(t) !== t.trim();
}

/** Un parámetro de plantilla limpio: una línea (`textoPlano`) y como mucho `max` caracteres. */
function limpiaTexto(s, max) {
  const t = textoPlano(s);
  return t.length > max ? t.slice(0, max).trim() : t;
}

/**
 * Grupo de un permiso: «A2» → "moto", «C+E» / «CE» → "camion", «D1» → "bus", «B» / «B+E» → "coche"; otro → null.
 * Acepta «permiso C», «C, C1» (se queda con el primero que encaje).
 */
function grupoDePermiso(permiso) {
  const trozos = String(permiso == null ? "" : permiso).toUpperCase().replace(/\s*\+\s*/g, "+").split(/[\s,;/]+/);
  for (const t of trozos) {
    if (/^(AM|A1|A2|A)$/.test(t)) return "moto";
    if (/^B(\+?E|96)?$/.test(t)) return "coche";
    if (/^C1?(\+?E)?$/.test(t)) return "camion";
    if (/^D1?(\+?E)?$/.test(t)) return "bus";
  }
  return null;
}

/**
 * Permisos candidatos del examen, por orden: el del examen (`permiso` o `aues.permiso`; si consta, SOLO ese) o los de
 * la ficha del alumno (`carnet_type` y `carnets_en_curso`). Sin repetidos ni vacíos.
 */
function permisosDeExamen({ examen, alumno } = {}) {
  const propio = (examen && (examen.permiso || (examen.aues && examen.aues.permiso))) || "";
  if (String(propio).trim()) return [String(propio).trim().toUpperCase()];
  const a = alumno || {};
  const lista = [a.carnet_type, ...(Array.isArray(a.carnets_en_curso) ? a.carnets_en_curso : [])];
  const out = [];
  for (const p of lista) {
    const t = typeof p === "string" ? p.trim().toUpperCase() : "";
    if (t && !out.includes(t)) out.push(t);
  }
  return out;
}

function normalizaCampos(x) {
  const o = esObjeto(x) ? x : {};
  return { lugar: limpiaTexto(o.lugar, LUGAR_MAX), llevar: limpiaTexto(o.llevar, LLEVAR_MAX) };
}

/**
 * La configuración guardada → { enabled, teorico: { lugar, llevar, por_permiso }, pista, circulacion } (siempre
 * completa). `por_permiso` solo guarda los grupos con algo escrito.
 */
function normalizaExamenLugar(raw) {
  const r = esObjeto(raw) ? raw : {};
  const out = { enabled: r.enabled === true };
  for (const p of PRUEBAS_LUGAR) {
    const x = esObjeto(r[p]) ? r[p] : {};
    const pp = esObjeto(x.por_permiso) ? x.por_permiso : {};
    const por_permiso = {};
    for (const g of GRUPOS_PERMISO) {
      const c = normalizaCampos(pp[g]);
      if (c.lugar || c.llevar) por_permiso[g] = c;
    }
    out[p] = { ...normalizaCampos(x), por_permiso };
  }
  return out;
}

function validaCampos(x, ruta) {
  const malas = Object.keys(x).filter((k) => k !== "lugar" && k !== "llevar" && !(ruta.split(".").length === 2 && k === "por_permiso"));
  if (malas.length) return `${ruta}: campos desconocidos: ${malas.join(", ")}`;
  for (const k of ["lugar", "llevar"]) {
    if (x[k] !== undefined && x[k] !== null && typeof x[k] !== "string") return `${ruta}.${k} debe ser texto`;
  }
  if (textoPlano(x.lugar).length > LUGAR_MAX) return `${ruta}.lugar: máximo ${LUGAR_MAX} caracteres`;
  if (textoPlano(x.llevar).length > LLEVAR_MAX) return `${ruta}.llevar: máximo ${LLEVAR_MAX} caracteres`;
  return null;
}

/** Lo que llega en el PUT → { value } (normalizado) | { error }. */
function validaExamenLugar(raw) {
  if (!esObjeto(raw)) return { error: "examen_lugar debe ser un objeto" };
  const desconocidas = Object.keys(raw).filter((k) => k !== "enabled" && !PRUEBAS_LUGAR.includes(k));
  if (desconocidas.length) return { error: `examen_lugar: campos desconocidos: ${desconocidas.join(", ")}` };
  if (raw.enabled !== undefined && typeof raw.enabled !== "boolean") return { error: "examen_lugar.enabled debe ser booleano" };
  for (const p of PRUEBAS_LUGAR) {
    const x = raw[p];
    if (x === undefined || x === null) continue;
    if (!esObjeto(x)) return { error: `examen_lugar.${p} debe ser un objeto` };
    const e = validaCampos(x, `examen_lugar.${p}`);
    if (e) return { error: e };
    const pp = x.por_permiso;
    if (pp === undefined || pp === null) continue;
    if (!esObjeto(pp)) return { error: `examen_lugar.${p}.por_permiso debe ser un objeto` };
    const gruposMalos = Object.keys(pp).filter((g) => !GRUPOS_PERMISO.includes(g));
    if (gruposMalos.length) return { error: `examen_lugar.${p}.por_permiso: grupos desconocidos: ${gruposMalos.join(", ")} (${GRUPOS_PERMISO.join(", ")})` };
    for (const g of GRUPOS_PERMISO) {
      const y = pp[g];
      if (y === undefined || y === null) continue;
      if (!esObjeto(y)) return { error: `examen_lugar.${p}.por_permiso.${g} debe ser un objeto` };
      const eg = validaCampos(y, `examen_lugar.${p}.por_permiso.${g}`);
      if (eg) return { error: eg };
    }
  }
  return { value: normalizaExamenLugar(raw) };
}

/** La prueba EXPLÍCITA del examen: la que puso la oficina (`prueba`) o la que trajo la DGT (`aues.prueba` D | C). */
function pruebaExplicita(examen) {
  if (!examen || examen.tipo !== "practico") return null;
  if (examen.prueba === "pista" || examen.prueba === "circulacion") return examen.prueba;
  const a = examen.aues && examen.aues.prueba;
  if (a === "D") return "pista";
  if (a === "C") return "circulacion";
  return null;
}

/** De qué fila de la configuración sale el lugar: teórico, pista o circulación (un práctico sin más = circulación). */
function pruebaLugarDeExamen(examen) {
  if (examen && examen.tipo === "teorico") return "teorico";
  return pruebaExplicita(examen) || "circulacion";
}

/** ¿Sale la plantilla nueva? Solo con el interruptor del lambda y la casilla de la autoescuela. */
function usaPlantillaLugar({ cfg, envActiva }) {
  return envActiva === true && normalizaExamenLugar(cfg).enabled;
}

/**
 * Grupo de permiso cuyo texto propio vale para este examen: el del primer permiso candidato que tenga texto propio en
 * esa prueba. null = se usa el de la prueba. `permiso`: texto o lista de candidatos (`permisosDeExamen`).
 */
function grupoAplicable(filaPrueba, permiso) {
  const candidatos = Array.isArray(permiso) ? permiso : [permiso];
  for (const p of candidatos) {
    const g = grupoDePermiso(p);
    if (g && filaPrueba.por_permiso[g]) return g;
  }
  return null;
}

/**
 * { lugar, llevar, lugarDe: "examen" | "config" | "pendiente", grupo } para ESTE examen. Nunca vacíos.
 * `permiso`: el permiso del examen (texto) o la lista de candidatos de `permisosDeExamen`.
 */
function infoExamen({ examen, cfg, lang, permiso = null }) {
  const l = idioma(lang);
  const fila = normalizaExamenLugar(cfg)[pruebaLugarDeExamen(examen)];
  const grupo = grupoAplicable(fila, permiso);
  const delGrupo = grupo ? fila.por_permiso[grupo] : { lugar: "", llevar: "" };
  const lugarCfg = delGrupo.lugar || fila.lugar;
  const propio = limpiaTexto(examen && examen.lugar, LUGAR_MAX);
  const lugar = propio || lugarCfg || LUGAR_PENDIENTE[l];
  return {
    lugar,
    llevar: delGrupo.llevar || fila.llevar || LLEVAR_DEFECTO[l],
    lugarDe: propio ? "examen" : lugarCfg ? "config" : "pendiente",
    grupo,
  };
}

/** {{4}} «martes 20/10/2026 a las 10:00» (sin hora: «… (hora por confirmar)»). */
function cuandoExamenLugar(examen, lang) {
  const l = idioma(lang);
  const hora = examen && examen.hora ? (l === "ca" ? ` a les ${examen.hora}` : ` a las ${examen.hora}`) : HORA_PENDIENTE[l];
  return `${fechaLarga(examen && examen.fecha, l)}${hora}`;
}

/**
 * Parámetros BASE de examen_recordatorio_lugar_crm (sin el negocio: c_sendTemplate lo antepone como {{1}}):
 * [alumno, «práctico de circulación, permiso B», «martes 20/10/2026 a las 10:00», lugar, qué llevar].
 * `permiso`: texto o lista (`permisosDeExamen`); en {{3}} solo se nombra si es UNO (con varios no se sabe cuál es).
 */
function paramsExamenLugar({ examen, nombre, cfg, lang, permiso = null }) {
  const l = idioma(lang);
  const alumno = limpiaTexto(nombre || (examen && examen.alumno_name), ALUMNO_MAX) || (l === "ca" ? "alumne" : "alumno");
  const pr = pruebaExplicita(examen);
  const lista = (Array.isArray(permiso) ? permiso : [permiso]).filter((p) => typeof p === "string" && p.trim());
  const unico = lista.length === 1 && grupoDePermiso(lista[0]) ? lista[0].trim().toUpperCase() : null;
  const tipo = limpiaTexto(descripcionExamen(pr ? { ...examen, prueba: pr } : examen, unico, l), 80);
  const info = infoExamen({ examen, cfg, lang: l, permiso: lista });
  return [alumno, tipo, cuandoExamenLugar(examen, l), info.lugar, info.llevar];
}

export {
  PRUEBAS_LUGAR, LUGAR_MAX, LLEVAR_MAX, ALUMNO_MAX, CUERPO_MAX, PLANTILLA_LUGAR, GRUPOS_PERMISO, PERMISOS_DE_GRUPO, LLEVAR_DEFECTO, LUGAR_PENDIENTE, textoPlano, cambiaAlAplanar, limpiaTexto, grupoDePermiso, permisosDeExamen, normalizaExamenLugar, validaExamenLugar, pruebaExplicita, pruebaLugarDeExamen, usaPlantillaLugar, grupoAplicable, infoExamen, cuandoExamenLugar, paramsExamenLugar,
};
